import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button, Text } from "@pentabd/ui";

import { InputTextRow, MultiFileRow, SelectControllerRow } from "@components";

import { useLanguage } from "@hooks/useLanguage";

import { FORM_FIELDS } from "@constants/form";

import { RationType } from "@type/HRM/family";
import {
  RationFormDataType,
  rationValidation,
} from "@validations/HRM/employee/familyInfoValidation";
import { BASE_DATA } from "@constants/base-data";
import { useBaseData } from "@hooks/useBaseData";
import { getConvertedOfficeData } from "@helpers/inventory/metaDataConvert";
import { convertDocumentData } from "@utils/documentDataConvert";
import { updateRationInfo } from "@api/family";
import { STATUS } from "@constants/status.hrm";

interface RationPropsType {
  rationInfo: RationType | null;
  getFamilyInfoRequestOngoing: boolean;
  updateRationRequestOngoing: boolean;
  isProfileDisabled: boolean;
  employeeMasterId: number | null;
  refetchRationData: (employeeID: number | null) => void;
  setIsSuccessModalOpen: (arg: boolean) => void;
  setIsErrorModalOpen: (arg: boolean) => void;
}

const {
  FAMILY_INFO: { RATION },
} = FORM_FIELDS.EMPLOYEE;

const { OFFICE } = BASE_DATA;

const { SUCCESS } = STATUS;

const RationForm = ({ isProfileDisabled }: { isProfileDisabled: boolean }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const { data: office } = useBaseData(OFFICE);

  const {
    register,
    formState: { errors },
    setValue,
    control,
  } = useFormContext<RationFormDataType>();

  return (
    <div className="d-flex flex-column gap-8">
      <Text weight="semibold" color="title" size="lg">
        {t("EMPLOYEE.FAMILY_INFO.FORM.RATION_INFO.HEADING")}
      </Text>

      <div className="d-flex flex-column gap-8">
        <InputTextRow
          register={{ ...register(RATION.ELIGIBLE_RATION_NUMBER) }}
          label="EMPLOYEE.FAMILY_INFO.FORM.RATION_INFO.ELIGIBLE_RATION_NUMBER"
          error={errors}
          required
          disabled={isProfileDisabled}
        />
        <InputTextRow
          register={{ ...register(RATION.RATION_CARD_NUMBER) }}
          label="EMPLOYEE.FAMILY_INFO.FORM.RATION_INFO.RATION_CARD_NUMBER"
          error={errors}
          required
          disabled={isProfileDisabled}
        />
        <SelectControllerRow
          label="EMPLOYEE.FAMILY_INFO.FORM.RATION_INFO.OFFICE"
          name={RATION.OFFICE_ID}
          error={errors}
          register={{ ...register(RATION.OFFICE_ID) }}
          options={getConvertedOfficeData(office, language)}
          setValue={setValue}
          control={control}
          isSearchable
          required
          disabled={isProfileDisabled}
        />
        <InputTextRow
          register={{ ...register(RATION.RECEIVING_POINT) }}
          label="EMPLOYEE.FAMILY_INFO.FORM.RATION_INFO.RECEIVING_POINT"
          error={errors}
          required
          disabled={isProfileDisabled}
        />
        <InputTextRow
          register={{ ...register(RATION.TOTAL_ELIGIBLE_MEMBER_FOR_RATION) }}
          label="EMPLOYEE.FAMILY_INFO.FORM.RATION_INFO.TOTAL_ELIGIBLE_MEMBER_FOR_RATION"
          error={errors}
          type="number"
          disabled={true}
        />
        <InputTextRow
          register={{ ...register(RATION.RATION_LAST_DATE) }}
          label="EMPLOYEE.FAMILY_INFO.FORM.RATION_INFO.RATION_LAST_DATE"
          error={errors}
          required
          disabled
        />
        <MultiFileRow
          name={RATION.DOCUMENTS}
          control={control}
          setValue={setValue}
          register={register}
          error={errors}
          fileDetails={["documentName", "date", "remarks", "memorialNo"]}
          disabled={isProfileDisabled}
        />
      </div>
    </div>
  );
};

const Ration = ({
  rationInfo,
  isProfileDisabled,
  employeeMasterId,
  refetchRationData,
  setIsErrorModalOpen,
  setIsSuccessModalOpen,
}: RationPropsType) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<RationType>({
    resolver: yupResolver(rationValidation),
    values: rationInfo as RationType,
  });

  const { handleSubmit, reset } = methods;
  const onSubmit: SubmitHandler<RationType> = (data: RationType) => {
    setIsLoading(true);
    const convertedData = convertDocumentData(data, RATION.DOCUMENTS);

    updateRationInfo(employeeMasterId, convertedData, (response: any) => {
      if (response.status === SUCCESS) {
        reset(response.data);
        refetchRationData(employeeMasterId);
        setIsSuccessModalOpen(true);
      } else {
        setIsErrorModalOpen(true);
      }
      setIsLoading(false);
    });
  };

  useEffect(() => reset(rationInfo ?? {}), [rationInfo, reset]);

  return (
    <FormProvider {...methods}>
      <form className="container" onSubmit={handleSubmit(onSubmit)}>
        <RationForm isProfileDisabled={isProfileDisabled} />

        <div className="row py-10">
          <div className="text-end col-12 col-lg-10">
            <Button
              loading={isLoading}
              htmlType="submit"
              fill="outline"
              type="primary"
              size="sm"
              disabled={isProfileDisabled}
            >
              {t("EMPLOYEE.FAMILY_INFO.FORM.SAVE_BUTTON")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default Ration;
